import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { useProfile } from "@/lib/ProfileContext";
import { GuidanceProvider, useGuidance } from "@/lib/GuidanceContext";
import { toast } from "sonner";
import { 
  LayoutDashboard, 
  Plus, 
  FileText, 
  Calendar, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  CreditCard,
  Target,
  Bell,
  Share2,
  Sparkles,
  Clock,
  Zap,
  MessageSquare,
  Radar,
  Quote,
  Lightbulb,
  Users,
  HelpCircle,
  BookOpen,
  AlertTriangle,
  AlertOctagon,
  Gift,
  Building2,
  Check,
  PlusCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import UserStatusBar from "@/components/UserStatusBar";
import ShareModal from "@/components/ShareModal";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/9fe16284-c883-46c5-9976-fb4189ef86a2/images/4414b575521af6b937b0721d2c61060a2fec8b4fac41d65ab6fa293761654c61.png";

// MVP Launch: Hiding Content Radar, Quote Across, Group Finder for cleaner initial experience
// These features will be enabled in a future update
const navItems = [
  { icon: HelpCircle, label: "How To Use", path: "#guide", isGuide: true },  // Quick Guide at top
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
  { icon: Plus, label: "Create", path: "/create" },
  { icon: Lightbulb, label: "Content Ideas", path: "/ideas", free: true },
  { icon: FileText, label: "Posts", path: "/posts" },
  { icon: Calendar, label: "Calendar", path: "/calendar" },
  { icon: Sparkles, label: "Viral Simulator", path: "/viral-simulator", free: true },
  { icon: Gift, label: "Rewards", path: "/rewards", badge: "Earn", free: true },
  // Auto-Reply moved to Admin Panel (/bajrangbali)
  // Hidden for MVP launch - uncomment when ready:
  // { icon: Radar, label: "Content Radar", path: "/radar" },
  // { icon: Quote, label: "Quote Across", path: "/quote-across" },
  // { icon: Users, label: "Group Finder", path: "/groups", free: true },
];

// Quick Guide content
const guideSteps = [
  {
    title: "1. Choose Your Input",
    icon: Plus,
    description: "Upload images, paste a URL, or speak/type your idea. Multiple images supported for collages!",
    tips: ["Drag & drop multiple images for collages", "Voice input for hands-free creation", "Works with blogs, products, and articles"]
  },
  {
    title: "2. Customize & Design",
    icon: FileText,
    description: "Choose AI Generated or 500 FREE Templates. Add listing details and drag fields to position them on your post.",
    tips: ["Drag & drop field positioning", "Custom text styling (font, color, size)", "Works for both AI and Templates"]
  },
  {
    title: "3. Add Your Personal Touch",
    icon: User,
    description: "Feature Yourself (add headshot) OR Personal Branding (banner with logo). Choose one for professional posts.",
    tips: ["Feature Yourself = headshot overlay", "Personal Branding = full banner", "Cannot use both together"]
  },
  {
    title: "4. Select Platforms",
    icon: Target,
    description: "Choose Facebook, Instagram, TikTok, Pinterest. Each gets optimized captions and hashtags.",
    tips: ["Multi-platform posting", "Platform-specific sizing", "Viral score predictions"]
  },
  {
    title: "5. Generate & Schedule",
    icon: Calendar,
    description: "Generate your content, then download or schedule directly to connected social accounts.",
    tips: ["Calendar view for planning", "Bulk content creation", "Connect accounts in Settings"]
  }
];

export default function DashboardLayout({ children }) {
  const location = useLocation();
  
  return (
    <GuidanceProvider currentPath={location.pathname}>
      <DashboardLayoutInner>{children}</DashboardLayoutInner>
    </GuidanceProvider>
  );
}

function DashboardLayoutInner({ children }) {
  const { user, logout } = useAuth();
  const { profiles, activeProfile, canCreateMore, profileLimit, switchProfile, loading: profilesLoading } = useProfile();
  const { glowTargets } = useGuidance();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showGuide, setShowGuide] = useState(false);  // Quick Guide modal
  const [systemStatus, setSystemStatus] = useState("healthy"); // healthy, degraded, unhealthy
  const [showProfileSwitcher, setShowProfileSwitcher] = useState(false);
  
  // Impersonation detection
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  
  useEffect(() => {
    const impersonationActive = localStorage.getItem("viraly_impersonation_active") === "true";
    const impersonatedUserData = localStorage.getItem("viraly_impersonation_user");
    
    if (impersonationActive && impersonatedUserData) {
      setIsImpersonating(true);
      try {
        setImpersonatedUser(JSON.parse(impersonatedUserData));
      } catch (e) {
        console.error("Failed to parse impersonated user data");
      }
    }
  }, []);
  
  const endImpersonation = async () => {
    const originalToken = localStorage.getItem("viraly_admin_original_token");
    const impersonationId = localStorage.getItem("viraly_impersonation_id");
    
    // Clean up impersonation data
    localStorage.removeItem("viraly_impersonation_active");
    localStorage.removeItem("viraly_impersonation_id");
    localStorage.removeItem("viraly_impersonation_user");
    localStorage.removeItem("viraly_token");
    
    // Try to end the session on backend
    if (impersonationId && originalToken) {
      try {
        await apiClient.post(`/admin/impersonation/${impersonationId}/end`, {}, {
          headers: { Authorization: `Bearer ${originalToken}` }
        });
      } catch (e) {
        console.error("Failed to end impersonation on backend");
      }
    }
    
    // Restore admin session if we have the original token
    if (originalToken) {
      localStorage.setItem("viraly_admin_token", originalToken);
      localStorage.removeItem("viraly_admin_original_token");
    }
    
    toast.success("Impersonation session ended");
    window.location.href = "/bajrangbali";
  };

  // Fetch system status on mount
  useEffect(() => {
    const checkSystemStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`);
        if (response.ok) {
          setSystemStatus("healthy");
        } else {
          setSystemStatus("degraded");
        }
      } catch {
        setSystemStatus("unhealthy");
      }
    };
    checkSystemStatus();
    // Check every 5 minutes
    const interval = setInterval(checkSystemStatus, 300000);
    return () => clearInterval(interval);
  }, []);

  // Fetch notifications from backend
  const [notificationData, setNotificationData] = useState({ total: 0, notifications: [], announcements: 0, promotions: 0 });
  
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await apiClient.get("/notifications/unread-count");
        setNotificationData(res.data);
      } catch (err) {
        // Use defaults
      }
    };
    fetchNotifications();
    // Refresh every 2 minutes
    const interval = setInterval(fetchNotifications, 120000);
    return () => clearInterval(interval);
  }, []);

  // Build notifications list
  const getNotificationIcon = (type) => {
    switch(type) {
      case 'post_created': return '✅';
      case 'post_published': return '📤';
      case 'purchase': return '💳';
      case 'smart_insight': return '💡';
      case 'warning': return '⚠️';
      case 'schedule_reminder': return '📅';
      default: return '🔔';
    }
  };
  
  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  const notifications = [
    // System status notification at top
    ...(systemStatus !== "healthy" ? [{
      id: "system",
      text: systemStatus === "degraded" 
        ? "Some features may be slower than usual. We're working on it."
        : "We're experiencing technical difficulties. Some features may be unavailable.",
      time: "Now",
      type: systemStatus === "degraded" ? "warning" : "error"
    }] : []),
    // Actual user notifications from backend
    ...(notificationData.notifications || []).slice(0, 5).map(n => ({
      id: n.id,
      text: n.message || n.title,
      time: getTimeAgo(n.created_at),
      type: n.type === 'warning' ? 'warning' : n.type === 'smart_insight' ? 'tip' : 'info',
      icon: getNotificationIcon(n.type),
      action_url: n.action_url
    })),
    // Dynamic counts
    ...(notificationData.announcements > 0 ? [{
      id: "announcements",
      text: `You have ${notificationData.announcements} new announcement${notificationData.announcements > 1 ? 's' : ''}`,
      time: "New",
      type: "info"
    }] : []),
    ...(notificationData.promotions > 0 ? [{
      id: "promotions",
      text: `${notificationData.promotions} active promotion${notificationData.promotions > 1 ? 's' : ''} available!`,
      time: "New",
      type: "tip"
    }] : []),
    ...(notificationData.has_policy_required ? [{
      id: "policy",
      text: "Please review and accept our updated policies",
      time: "Important",
      type: "error"
    }] : []),
    // Fallback if no notifications
    ...(notificationData.total === 0 && systemStatus === "healthy" && (!notificationData.notifications || notificationData.notifications.length === 0) ? [{
      id: "empty",
      text: "You're all caught up! No new notifications.",
      time: "",
      type: "info"
    }] : [])
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleShare = async () => {
    // On mobile with native share support, use it
    if (navigator.share && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      const shareData = {
        title: 'Viraly - AI Social Media Automation',
        text: 'I\'m automating my social media with Viraly! Get 5 free posts to start:',
        url: window.location.origin
      };
      try {
        await navigator.share(shareData);
      } catch (error) {
        // User cancelled
      }
    } else {
      // On desktop, show our custom share modal
      setShowShareModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-viraly-dark flex flex-col">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 text-center text-sm flex items-center justify-center gap-4 fixed top-0 left-0 right-0 z-50" data-testid="impersonation-banner">
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <strong>Admin View:</strong> You are viewing as {impersonatedUser?.name || impersonatedUser?.email || "User"} ({impersonatedUser?.plan || "free"} plan)
          </span>
          <button
            onClick={endImpersonation}
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors"
            data-testid="end-impersonation-btn"
          >
            <X className="w-3 h-3" />
            Exit View
          </button>
        </div>
      )}
      
      <div className={`flex flex-1 ${isImpersonating ? 'pt-10' : ''}`}>
      {/* Sidebar - Desktop */}
      <aside className={`hidden lg:flex flex-col w-64 bg-viraly-card border-r border-white/5 fixed h-full ${isImpersonating ? 'top-10' : ''}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Viraly" className="h-10 w-10" />
            <span className="font-heading font-bold text-2xl gradient-text">Viraly</span>
          </Link>
        </div>
        
        <nav className="flex-1 px-4">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              
              // Special handling for Quick Guide
              if (item.isGuide) {
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => setShowGuide(true)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-viraly-orange hover:text-white hover:bg-viraly-orange/10 border border-viraly-orange/30"
                      data-testid="nav-guide"
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                      <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold bg-viraly-orange/20 text-viraly-orange">
                        GUIDE
                      </span>
                    </button>
                  </li>
                );
              }
              
              return (
                <li key={item.path}>
                  <a
                    href={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive 
                        ? "bg-viraly-gradient text-white" 
                        : "text-viraly-muted hover:text-white hover:bg-white/5"
                    } ${
                      // Add glow guidance classes (when not active)
                      !isActive && item.label === "Create" && glowTargets?.create ? "glow-guide-primary" : ""
                    } ${
                      !isActive && item.label === "Posts" && glowTargets?.posts ? "glow-guide" : ""
                    } ${
                      !isActive && item.label === "Calendar" && glowTargets?.calendar ? "glow-guide" : ""
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold bg-viraly-orange/20 text-viraly-orange">
                        {item.badge}
                      </span>
                    )}
                    {item.free && !item.badge && (
                      <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold bg-viraly-success/20 text-viraly-success">
                        FREE
                      </span>
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
        
        <div className="p-4 border-t border-white/5">
          <a
            href="/settings"
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              location.pathname === "/settings"
                ? "bg-viraly-gradient text-white"
                : "text-viraly-muted hover:text-white hover:bg-white/5"
            }`}
            data-testid="nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </a>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="absolute left-0 top-0 h-full w-64 bg-viraly-card border-r border-white/5 animate-slide-up">
            <div className="p-6 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img src={LOGO_URL} alt="Viraly" className="h-10 w-10" />
                <span className="font-heading font-bold text-2xl gradient-text">Viraly</span>
              </Link>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="w-6 h-6 text-viraly-muted" />
              </button>
            </div>
            
            <nav className="px-4">
              <ul className="space-y-1">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  
                  // Special handling for Quick Guide in mobile too
                  if (item.isGuide) {
                    return (
                      <li key={item.path}>
                        <button
                          onClick={() => {
                            setSidebarOpen(false);
                            setShowGuide(true);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-viraly-orange hover:text-white hover:bg-viraly-orange/10 border border-viraly-orange/30"
                        >
                          <item.icon className="w-5 h-5" />
                          <span className="font-medium">{item.label}</span>
                          <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold bg-viraly-orange/20 text-viraly-orange">
                            GUIDE
                          </span>
                        </button>
                      </li>
                    );
                  }
                  
                  return (
                    <li key={item.path}>
                      <a
                        href={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                          isActive 
                            ? "bg-viraly-gradient text-white" 
                            : "text-viraly-muted hover:text-white hover:bg-white/5"
                        } ${
                          // Add glow guidance classes for mobile
                          !isActive && item.label === "Create" && glowTargets?.create ? "glow-guide-primary" : ""
                        } ${
                          !isActive && item.label === "Posts" && glowTargets?.posts ? "glow-guide" : ""
                        } ${
                          !isActive && item.label === "Calendar" && glowTargets?.calendar ? "glow-guide" : ""
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span>{item.label}</span>
                        {item.badge && (
                          <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold bg-viraly-orange/20 text-viraly-orange">
                            {item.badge}
                          </span>
                        )}
                        {item.free && !item.badge && (
                          <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold bg-viraly-success/20 text-viraly-success">
                            FREE
                          </span>
                        )}
                      </a>
                    </li>
                  );
                })}
                <li>
                  <a
                    href="/settings"
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      location.pathname === "/settings"
                        ? "bg-viraly-gradient text-white"
                        : "text-viraly-muted hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Settings className="w-5 h-5" />
                    <span>Settings</span>
                  </a>
                </li>
              </ul>
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 glass-card border-0 border-b border-white/5 px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="hidden lg:block" />
            
            <div className="flex items-center gap-4">
              {/* Profile Switcher */}
              <DropdownMenu open={showProfileSwitcher} onOpenChange={setShowProfileSwitcher}>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
                    data-testid="profile-switcher"
                  >
                    <Building2 className="w-4 h-4 text-viraly-orange" />
                    <span className="text-sm font-medium max-w-[150px] truncate">
                      {activeProfile?.name || "My Brand"}
                    </span>
                    <ChevronDown className="w-4 h-4 text-viraly-muted" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 bg-viraly-card border-white/10">
                  <div className="px-3 py-2 border-b border-white/10">
                    <p className="text-xs text-viraly-muted">Switch Profile</p>
                    <p className="text-xs text-viraly-orange">{profiles.length} of {profileLimit} profiles used</p>
                  </div>
                  {profiles.map((profile) => (
                    <DropdownMenuItem
                      key={profile.id}
                      onClick={() => {
                        switchProfile(profile.id);
                        setShowProfileSwitcher(false);
                      }}
                      className={`flex items-center justify-between cursor-pointer ${
                        activeProfile?.id === profile.id ? "bg-viraly-orange/10" : ""
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                          {profile.logo_url ? (
                            <img src={profile.logo_url} alt="" className="w-6 h-6 rounded object-cover" />
                          ) : (
                            <Building2 className="w-4 h-4 text-viraly-muted" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{profile.name}</p>
                          {profile.niche && (
                            <p className="text-xs text-viraly-muted">{profile.niche}</p>
                          )}
                        </div>
                      </div>
                      {activeProfile?.id === profile.id && (
                        <Check className="w-4 h-4 text-viraly-orange" />
                      )}
                    </DropdownMenuItem>
                  ))}
                  {canCreateMore && (
                    <>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem
                        onClick={() => {
                          setShowProfileSwitcher(false);
                          navigate("/settings?tab=profiles&action=create");
                        }}
                        className="flex items-center gap-2 cursor-pointer text-viraly-orange"
                      >
                        <PlusCircle className="w-4 h-4" />
                        <span>Create New Profile</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={() => {
                      setShowProfileSwitcher(false);
                      navigate("/settings?tab=profiles");
                    }}
                    className="flex items-center gap-2 cursor-pointer text-viraly-muted"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Manage Profiles</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <button 
                onClick={handleShare}
                className="p-2 rounded-xl hover:bg-white/5 transition-colors text-viraly-muted hover:text-white"
                title="Share Viraly"
                data-testid="share-btn"
              >
                <Share2 className="w-5 h-5" />
              </button>
              
              {/* Notification Bell with System Status Indicator */}
              <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="relative p-2 rounded-xl hover:bg-white/10 transition-colors group"
                    title={systemStatus === "healthy" ? "Notifications" : "System Alert - Click for details"}
                    data-testid="notification-bell"
                  >
                    <Bell className={`w-5 h-5 transition-colors ${
                      systemStatus === "unhealthy" ? "text-red-500" :
                      systemStatus === "degraded" ? "text-yellow-500" :
                      "text-white group-hover:text-viraly-orange"
                    }`} />
                    {(notificationData.total > 0 || systemStatus !== "healthy") && (
                      <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold ring-2 ring-viraly-dark ${
                        systemStatus === "unhealthy" ? "bg-red-500 animate-pulse" :
                        systemStatus === "degraded" ? "bg-yellow-500 animate-pulse" :
                        notificationData.has_policy_required ? "bg-red-500" :
                        "bg-viraly-orange"
                      }`}>
                        {notificationData.total > 9 ? "9+" : notificationData.total || "!"}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 bg-viraly-card border-white/10 p-0">
                  <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <p className="font-medium">Notifications</p>
                    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-xs ${
                      systemStatus === "healthy" ? "bg-green-500/20 text-green-400" :
                      systemStatus === "degraded" ? "bg-yellow-500/20 text-yellow-400" :
                      "bg-red-500/20 text-red-400"
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        systemStatus === "healthy" ? "bg-green-500" :
                        systemStatus === "degraded" ? "bg-yellow-500" :
                        "bg-red-500"
                      }`} />
                      {systemStatus === "healthy" ? "All Systems Go" : 
                       systemStatus === "degraded" ? "Partial Outage" : "System Issues"}
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.map((notif) => (
                      <div key={notif.id} className={`px-4 py-3 hover:bg-white/5 cursor-pointer border-b border-white/5 ${
                        notif.type === 'error' ? 'bg-red-500/5' : 
                        notif.type === 'warning' ? 'bg-yellow-500/5' : ''
                      }`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            notif.type === 'success' ? 'bg-viraly-success/20' :
                            notif.type === 'tip' ? 'bg-viraly-orange/20' : 
                            notif.type === 'warning' ? 'bg-yellow-500/20' :
                            notif.type === 'error' ? 'bg-red-500/20' : 'bg-white/10'
                          }`}>
                            {notif.type === 'success' ? <Zap className="w-4 h-4 text-viraly-success" /> :
                             notif.type === 'tip' ? <Sparkles className="w-4 h-4 text-viraly-orange" /> :
                             notif.type === 'warning' ? <AlertTriangle className="w-4 h-4 text-yellow-500" /> :
                             notif.type === 'error' ? <AlertOctagon className="w-4 h-4 text-red-500" /> :
                             <Bell className="w-4 h-4 text-viraly-muted" />}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm">{notif.text}</p>
                            <p className="text-xs text-viraly-muted mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {notif.time}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-2 border-t border-white/10">
                    <button 
                      onClick={() => setShowNotifications(false)}
                      className="text-sm text-viraly-orange hover:underline w-full text-center"
                    >
                      View all notifications
                    </button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-viraly-gradient flex items-center justify-center">
                      <span className="text-sm font-bold text-white">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden md:block text-sm">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-viraly-muted" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-viraly-card border-white/10">
                  <div className="px-3 py-2">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-viraly-muted">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings?tab=billing")} className="cursor-pointer">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Billing
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-viraly-error">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content - Key by location to force re-render on route change */}
        <UserStatusBar />
        <main key={location.pathname} className="overflow-y-auto max-h-[calc(100vh-80px)] pb-8">{children}</main>
      </div>
      </div>
      {/* Chatbot is now global in App.js via ViraChatbot component */}

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)}
        shareData={{
          title: "Viraly - AI Social Media Automation",
          text: "I'm automating my social media with Viraly! Check it out - get 5 free posts to start!",
          url: window.location.origin
        }}
      />

      {/* Quick Guide Modal */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-viraly-card border-viraly-border">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading flex items-center gap-3">
              <BookOpen className="w-7 h-7 text-viraly-orange" />
              <span className="gradient-text">How To Use Viraly</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {guideSteps.map((step, index) => (
              <div 
                key={index}
                className="bg-viraly-dark/50 rounded-xl p-5 border border-white/5 hover:border-viraly-orange/30 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-viraly-orange/10 text-viraly-orange">
                    <step.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-viraly-muted mb-3">{step.description}</p>
                    <div className="space-y-1">
                      {step.tips.map((tip, tipIdx) => (
                        <div key={tipIdx} className="flex items-center gap-2 text-sm text-viraly-muted/80">
                          <Zap className="w-3 h-3 text-viraly-orange" />
                          <span>{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Quick Links */}
            <div className="bg-viraly-gradient/10 rounded-xl p-5 border border-viraly-orange/20">
              <h3 className="text-lg font-semibold text-white mb-3">Quick Links</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <a href="/create" className="flex items-center gap-2 px-3 py-2 bg-viraly-dark rounded-lg hover:bg-white/5 transition-all">
                  <Plus className="w-4 h-4 text-viraly-orange" />
                  <span className="text-sm text-white">Create Post</span>
                </a>
                <a href="/posts" className="flex items-center gap-2 px-3 py-2 bg-viraly-dark rounded-lg hover:bg-white/5 transition-all">
                  <FileText className="w-4 h-4 text-viraly-orange" />
                  <span className="text-sm text-white">My Posts</span>
                </a>
                <a href="/settings" className="flex items-center gap-2 px-3 py-2 bg-viraly-dark rounded-lg hover:bg-white/5 transition-all">
                  <Settings className="w-4 h-4 text-viraly-orange" />
                  <span className="text-sm text-white">Settings</span>
                </a>
                <a href="/ideas" className="flex items-center gap-2 px-3 py-2 bg-viraly-dark rounded-lg hover:bg-white/5 transition-all">
                  <Lightbulb className="w-4 h-4 text-viraly-orange" />
                  <span className="text-sm text-white">Content Ideas</span>
                </a>
              </div>
            </div>
            
            {/* Support */}
            <div className="text-center text-sm text-viraly-muted">
              Need help? <button onClick={() => {
                setShowGuide(false); // Close the guide modal first
                window.dispatchEvent(new CustomEvent('openViraChat'));
              }} className="text-viraly-orange hover:underline">Chat with Vira</button> - our AI assistant is here 24/7!
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
